"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePostHog } from "@/hooks/usePostHog";

interface FooterProps {
  footerContent?: {
    copyright?: string
    columns?: Array<{
      title?: string
      links?: Array<{ text: string; href: string; enabled?: boolean }>
    }>
    links?: Array<{ text: string; href: string; enabled?: boolean }>
  }
  newsletterContent?: {
    title?: string
    placeholder?: string
  }
  hidePreguntasAndComprar?: boolean
}

export default function Footer({ footerContent, newsletterContent, hidePreguntasAndComprar }: FooterProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState("");
  const { track, identify } = usePostHog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    
    try {
      // Track newsletter signup attempt
      track('newsletter_signup_attempted', {
        email: email,
        location: 'footer',
        landing_page_variant: 'S'
      });

      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        
        // Track successful signup
        track('newsletter_signup_completed', {
          email: email,
          location: 'footer',
          landing_page_variant: 'S'
        });

        // Identify user in PostHog (explicit consent via signup)
        identify(email, {
          signup_source: 'footer_newsletter',
          marketing_opt_in: true,
        });
        // Merge anonymous history into this identity
        try { (window as any).posthog?.alias?.(email) } catch {}

        // Discrete success feedback - show success for 3 seconds then reset
        setTimeout(() => {
          setStatus('idle');
          setEmail("");
          setMessage("");
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.error || 'Error al suscribirse');
        
        // Track failed signup
        track('newsletter_signup_failed', {
          email: email,
          location: 'footer',
          error: data.error || 'Unknown error'
        });

        setTimeout(() => {
          setStatus('idle');
          setMessage("");
        }, 3000);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Error de conexión');
      
      track('newsletter_signup_failed', {
        email: email,
        location: 'footer',
        error: 'Network error'
      });

      setTimeout(() => {
        setStatus('idle');
        setMessage("");
      }, 3000);
    }
  };

  return (
    <footer className="bg-[#E8E6E0] py-16">
      <div className="container-page">
        {/* Top Section */}
        <div className="grid lg:grid-cols-[1fr_auto] gap-12 mb-16 items-start">
          {/* Newsletter Subscription */}
          <div className="max-w-sm">
            <h3 className="text-lg font-mono font-bold mb-4">
              {newsletterContent?.title || "Suscríbete y obtén 10% de descuento en tu primera orden y comienza a nutrir tu cabello desde la raíz"}
            </h3>
            <form onSubmit={handleSubmit} className="relative">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={
                    status === 'success' 
                      ? "✓ ¡Listo!" 
                      : status === 'error' && message 
                        ? message 
                        : newsletterContent?.placeholder || "Tu e-mail"
                  }
                  className={`w-full px-4 py-3 pr-12 rounded-full border font-mono focus:outline-none transition-all duration-300 ${
                    status === 'success' 
                      ? 'border-green-500 bg-green-50 text-green-700 placeholder:text-green-600' 
                      : status === 'error' 
                        ? 'border-red-400 bg-red-50 placeholder:text-red-600' 
                        : status === 'loading'
                          ? 'border-blue-400 bg-blue-50 animate-pulse'
                          : 'border-gray-400 bg-transparent placeholder:text-gray-600 focus:border-black'
                  }`}
                  disabled={status === 'loading' || status === 'success'}
                  required
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || status === 'success' || !email.trim()}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-300 ${
                    status === 'loading' 
                      ? 'opacity-50 cursor-not-allowed' 
                      : status === 'success'
                        ? 'text-green-600 transform scale-110'
                        : 'hover:opacity-70 hover:scale-105'
                  }`}
                >
                  {status === 'loading' ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : status === 'success' ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
              {message && status !== 'success' && (
                <p className={`text-xs font-mono mt-2 ${
                  status === 'error' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {message}
                </p>
              )}
            </form>
          </div>

          {/* Right side: Navigation + Hashtag */}
          <div className="flex flex-col items-end gap-8">
            {/* Hashtag */}
            <h2 className="text-3xl font-mono font-bold">#Olin1</h2>
            
            {/* Navigation Links from Sanity (columns) */}
            {footerContent?.columns && footerContent.columns.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-right">
                {footerContent.columns.map((col, idx) => (
                  <div key={idx} className="space-y-3">
                    {col.links
                      ?.filter(l => l.enabled !== false)
                      .filter(l => !hidePreguntasAndComprar || (l.text !== "Preguntas" && !l.href.includes("comprar")))
                      .map((l, i) => (
                        <a key={i} href={l.href} className="block font-mono text-sm hover:opacity-70">{l.text}</a>
                      ))}
                  </div>
                ))}
              </div>
            ) : footerContent?.links && footerContent.links.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-right">
                <div className="space-y-3">
                  {footerContent.links
                    .filter(l => !hidePreguntasAndComprar || (l.text !== "Preguntas" && !l.href.includes("comprar")))
                    .slice(0, Math.ceil(footerContent.links.length/2))
                    .map((l, i) => (
                      <a key={i} href={l.href} className="block font-mono text-sm hover:opacity-70">{l.text}</a>
                    ))}
                </div>
                <div className="space-y-3">
                  {footerContent.links
                    .filter(l => !hidePreguntasAndComprar || (l.text !== "Preguntas" && !l.href.includes("comprar")))
                    .slice(Math.ceil(footerContent.links.length/2))
                    .map((l, i) => (
                      <a key={i} href={l.href} className="block font-mono text-sm hover:opacity-70">{l.text}</a>
                    ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-right">
                <div className="space-y-3">
                  {!hidePreguntasAndComprar && <a href="#gestionar-suscripcion" className="block font-mono text-sm hover:opacity-70">Gestionar Suscripción</a>}
                  <a href="#ingredientes" className="block font-mono text-sm hover:opacity-70">Ingredientes</a>
                  <a href="#ciencia" className="block font-mono text-sm hover:opacity-70">Ciencia</a>
                  <a href="#partners" className="block font-mono text-sm hover:opacity-70">Partners</a>
                </div>
                <div className="space-y-3">
                  <a href="#nosotros" className="block font-mono text-sm hover:opacity-70">Nosotros</a>
                  <a href="https://www.instagram.com/olinlabsmex/" className="block font-mono text-sm hover:opacity-70">Instagram</a>
                  <a href="https://facebook.com/olinlabsmx" className="block font-mono text-sm hover:opacity-70">Facebook</a>
                  <a href="https://tiktok.com/@olinlabsmx" className="block font-mono text-sm hover:opacity-70">TikTok</a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section - Logos */}
        <div className="flex items-end justify-between mb-4">
          {/* Olin Labs Logo */}
          <div>
            <Link href="/" aria-label="Ir al inicio">
              <Image
                src="/images/olin-labs-logo-final.svg"
                alt="Olin Labs"
                width={120}
                height={64}
                className="w-auto h-16 object-contain"
              />
            </Link>
          </div>

          {/* Sunburst Icon */}
          <div>
            <Image
              src="/images/sunburst_black.svg"
              alt=""
              width={80}
              height={80}
              className="object-contain"
            />
          </div>
        </div>

        {/* Legal Section */}
        {(footerContent?.copyright) && (
          <div className="text-left">
            <p className="text-xs text-gray-600 font-mono">
              © {new Date().getFullYear()} {footerContent.copyright}
            </p>
            <p className="text-xs text-gray-600 font-mono mt-1">
              <a href="/terminos-y-condiciones" className="hover:opacity-70">Términos y Condiciones</a>
              <span className="mx-2">·</span>
              <a href="/politica-de-devolucion" className="hover:opacity-70">Política de Devolución</a>
            </p>
            <p className="text-xs text-gray-600 font-mono mt-1">
              <a href="/politica-de-privacidad" className="hover:opacity-70">Política de Privacidad</a>
              <span className="mx-2">·</span>
              <a href="/politica-de-cookies" className="hover:opacity-70">Política de Cookies</a>
            </p>
          </div>
        )}
      </div>
    </footer>
  );
}
